/**
 * Copyright © Veeam Software Group GmbH.
 */

import { observer } from 'mobx-react-lite';
import React from 'react';
import {
    Form,
    FormLayout,
    FieldLayout,
    StackView,
    STACK_DIRECTION,
    STACK_GAP,
    Checkbox,
    CONTROL_SIZE,
    LinkButton,
    NoLabelGroup,
    Combobox,
    Radiobutton,
    NumberInput,
    usePageWizardStore,
    FormValidator,
    isValid,
    constants,
    useGlobalLang,
} from '@veeam-vspc/shared/components';
import {
    Vb365BackupJobSchedulePolicyDailyType,
    Vb365BackupJobSchedulePolicyPeriodicallyEvery,
    Vb365BackupJobSchedulePolicySchedulePolicyType,
} from '@veeam-vspc/models/rest';

import type { WizardStep, WizardStepData, FormErrors } from '@veeam-vspc/shared/components';
import type { Vb365BackupJob, Vb365BackupJobSchedulePolicy } from '@veeam-vspc/models/rest';
import type { VspcLang } from 'configs/languages';

import { TimeField } from 'components/controls/TimeField';
import { StepLayout } from 'components/layouts/StepLayout';
import { useJobWizardStore } from '../../stores';
import { getPeriodicallyReadableValues, isPeriodicScheduleApplicable } from '../../../../helpers';
import { ConfigureSchedule, ConfigureBackupWindow } from './components';

import type { JobWizardStore } from '../../stores';

export const stepValidate = (data: Vb365BackupJobSchedulePolicy): FormErrors<Vb365BackupJobSchedulePolicy> => {
    const validator = new FormValidator(data);

    if (data.retryEnabled) {
        validator
            .validate('retryNumber')
            .number()
            .max(777)
            .min(1);

        validator
            .validate('retryWaitInterval')
            .number()
            .max(999)
            .min(1);
    }

    return validator.result();
};

export const getScheduleStep = (lang: VspcLang, store: JobWizardStore): WizardStep<Vb365BackupJob> => ({
    title: lang.SCHEDULE,
    isHidden: () => store.isScheduleHidden,
    render: data => <ScheduleStep {...data} />,
    validate: () => {
        const validationResult = isValid(stepValidate, store.data.schedulePolicy);

        return validationResult;
    },
});

const ScheduleStep = observer(({ validationState }: WizardStepData<Vb365BackupJob>) => {
    const lang = useGlobalLang<VspcLang>();
    const wizardStore = useJobWizardStore();
    const wizardContext = usePageWizardStore();

    const handleDataChange = (data: Vb365BackupJobSchedulePolicy) => {
        wizardStore.updateSchedule({
            ...data,
            backupWindowSettings: wizardStore.data.schedulePolicy.backupWindowSettings,
            periodicallyWindowSettings: wizardStore.data.schedulePolicy.periodicallyWindowSettings,
            periodicallyOffsetMinutes: wizardStore.data.schedulePolicy.periodicallyOffsetMinutes,
            periodicallyWindowEnabled: data.schedulePolicyType === Vb365BackupJobSchedulePolicySchedulePolicyType.Periodically &&
                [
                    Vb365BackupJobSchedulePolicyPeriodicallyEvery.Hours1,
                    Vb365BackupJobSchedulePolicyPeriodicallyEvery.Hours2,
                    Vb365BackupJobSchedulePolicyPeriodicallyEvery.Hours4,
                    Vb365BackupJobSchedulePolicyPeriodicallyEvery.Hours8,
                ].includes(data.periodicallyEvery),
        });
    };

    return (
        <StepLayout
            title={lang.SCHEDULE}
            description={lang.CHOOSE_WHEN_YOU_WANT_BACKUP_JOB}
        >
            <Form
                value={wizardStore.data.schedulePolicy}
                validationState={validationState}
                validate={stepValidate}
                onChange={handleDataChange}
            >

                <FormLayout inlineLabel>
                    <NoLabelGroup
                        content={(
                            <Checkbox name='scheduleEnabled'>
                                {lang.RUN_THE_JOB_AUTOMATICALLY}
                            </Checkbox>
                        )}
                        subContent={(
                            <StackView gap={STACK_GAP.m} direction={STACK_DIRECTION.column}>
                                <FormLayout direction={STACK_DIRECTION.row}>
                                    <FieldLayout
                                        disabled={!wizardStore.data.schedulePolicy.scheduleEnabled}
                                    >
                                        <Radiobutton
                                            name='schedulePolicyType'
                                            value={Vb365BackupJobSchedulePolicySchedulePolicyType.Daily}
                                        >
                                            {lang.DAILY_AT_THIS_TIME}
                                        </Radiobutton>
                                    </FieldLayout>

                                    <FieldLayout
                                        disabled={!wizardStore.data.schedulePolicy.scheduleEnabled
                                            || wizardStore.data.schedulePolicy.schedulePolicyType
                                                !== Vb365BackupJobSchedulePolicySchedulePolicyType.Daily}
                                    >
                                        <TimeField
                                            name='dailyTime'
                                            value={wizardStore.data.schedulePolicy.dailyTime}
                                            shortFormat
                                            minutesInterval={15}
                                            utc
                                        />
                                    </FieldLayout>

                                    <FieldLayout
                                        disabled={!wizardStore.data.schedulePolicy.scheduleEnabled
                                            || wizardStore.data.schedulePolicy.schedulePolicyType
                                                !== Vb365BackupJobSchedulePolicySchedulePolicyType.Daily}
                                    >
                                        <Combobox
                                            name='dailyType'
                                            value={wizardStore.data.schedulePolicy.dailyType}
                                            onChange={value => wizardStore.updateSchedule({ ...wizardStore.data.schedulePolicy, dailyType: value })}
                                            data={[
                                                { value: Vb365BackupJobSchedulePolicyDailyType.Everyday, text: lang.EVERYDAY },
                                                { value: Vb365BackupJobSchedulePolicyDailyType.Workdays, text: lang.WORKDAYS },
                                                { value: Vb365BackupJobSchedulePolicyDailyType.Weekends, text: lang.WEEKENDS },
                                                { value: Vb365BackupJobSchedulePolicyDailyType.Monday, text: lang.MONDAY },
                                                { value: Vb365BackupJobSchedulePolicyDailyType.Tuesday, text: lang.TUESDAY },
                                                { value: Vb365BackupJobSchedulePolicyDailyType.Wednesday, text: lang.WEDNESDAY },
                                                { value: Vb365BackupJobSchedulePolicyDailyType.Thursday, text: lang.THURSDAY },
                                                { value: Vb365BackupJobSchedulePolicyDailyType.Friday, text: lang.FRIDAY },
                                                { value: Vb365BackupJobSchedulePolicyDailyType.Saturday, text: lang.SATURDAY },
                                                { value: Vb365BackupJobSchedulePolicyDailyType.Sunday, text: lang.SUNDAY },
                                            ]}
                                            valueGetter={x => x.value}
                                            textGetter={x => x.text}
                                        />
                                    </FieldLayout>
                                </FormLayout>

                                <FormLayout direction={STACK_DIRECTION.row}>
                                    <FieldLayout
                                        disabled={!wizardStore.data.schedulePolicy.scheduleEnabled}
                                    >
                                        <Radiobutton
                                            name='schedulePolicyType'
                                            value={Vb365BackupJobSchedulePolicySchedulePolicyType.Periodically}
                                        >
                                            {lang.PERIODICALLY_EVERY}
                                        </Radiobutton>
                                    </FieldLayout>

                                    <Combobox
                                        name='periodicallyEvery'
                                        value={wizardStore.data.schedulePolicy.periodicallyEvery}
                                        onChange={value => wizardStore.updateSchedule({
                                            ...wizardStore.data.schedulePolicy,
                                            periodicallyEvery: value,
                                        })}
                                        data={[
                                            {
                                                value: Vb365BackupJobSchedulePolicyPeriodicallyEvery.Minutes5,
                                                text: getPeriodicallyReadableValues(lang)[Vb365BackupJobSchedulePolicyPeriodicallyEvery.Minutes5],
                                            },
                                            {
                                                value: Vb365BackupJobSchedulePolicyPeriodicallyEvery.Minutes10,
                                                text: getPeriodicallyReadableValues(lang)[Vb365BackupJobSchedulePolicyPeriodicallyEvery.Minutes10],
                                            },
                                            {
                                                value: Vb365BackupJobSchedulePolicyPeriodicallyEvery.Minutes15,
                                                text: getPeriodicallyReadableValues(lang)[Vb365BackupJobSchedulePolicyPeriodicallyEvery.Minutes15],
                                            },
                                            {
                                                value: Vb365BackupJobSchedulePolicyPeriodicallyEvery.Minutes30,
                                                text: getPeriodicallyReadableValues(lang)[Vb365BackupJobSchedulePolicyPeriodicallyEvery.Minutes30],
                                            },
                                            {
                                                value: Vb365BackupJobSchedulePolicyPeriodicallyEvery.Hours1,
                                                text: getPeriodicallyReadableValues(lang)[Vb365BackupJobSchedulePolicyPeriodicallyEvery.Hours1],
                                            },
                                            {
                                                value: Vb365BackupJobSchedulePolicyPeriodicallyEvery.Hours2,
                                                text: getPeriodicallyReadableValues(lang)[Vb365BackupJobSchedulePolicyPeriodicallyEvery.Hours2],
                                            },
                                            {
                                                value: Vb365BackupJobSchedulePolicyPeriodicallyEvery.Hours4,
                                                text: getPeriodicallyReadableValues(lang)[Vb365BackupJobSchedulePolicyPeriodicallyEvery.Hours4],
                                            },
                                            {
                                                value: Vb365BackupJobSchedulePolicyPeriodicallyEvery.Hours8,
                                                text: getPeriodicallyReadableValues(lang)[Vb365BackupJobSchedulePolicyPeriodicallyEvery.Hours8],
                                            },
                                        ]}
                                        valueGetter={x => x.value}
                                        textGetter={x => x.text}
                                        size={CONTROL_SIZE.xs}
                                        disabled={!wizardStore.data.schedulePolicy.scheduleEnabled
                                            || wizardStore.data.schedulePolicy.schedulePolicyType
                                                !== Vb365BackupJobSchedulePolicySchedulePolicyType.Periodically}
                                    />

                                    <LinkButton
                                        onClick={() => wizardContext.openPanel(hide => <ConfigureSchedule hidePanel={hide} />)}
                                        disabled={!isPeriodicScheduleApplicable(wizardStore.data.schedulePolicy)}
                                        style={{ width: 'fit-content' }}
                                    >
                                        {lang.SET_SCHEDULE}
                                    </LinkButton>
                                </FormLayout>
                            </StackView>
                        )}
                    />

                    <NoLabelGroup
                        content={(
                            <Checkbox name='retryEnabled'>
                                {lang.AUTOMATIC_RETRY}
                            </Checkbox>
                        )}
                        subContent={(
                            <FormLayout inlineLabel style={{ marginLeft: `-${constants.SPACING_S}` }}>
                                <FieldLayout
                                    label={`${lang.RETRY_FAILED_OBJECTS}:`}
                                    disabled={!wizardStore.data.schedulePolicy.retryEnabled}
                                    inlineLabel
                                >
                                    <NumberInput
                                        name='retryNumber'
                                        value={wizardStore.data.schedulePolicy.retryNumber}
                                        inlineLabel
                                        suffix={lang.TIMES}
                                        minValue={1}
                                        maxValue={777}
                                    />
                                </FieldLayout>

                                <FieldLayout
                                    label={`${lang.WAIT_BEFORE_RETRY}:`}
                                    disabled={!wizardStore.data.schedulePolicy.retryEnabled}
                                    inlineLabel
                                >
                                    <NumberInput
                                        name='retryWaitInterval'
                                        value={wizardStore.data.schedulePolicy.retryWaitInterval}
                                        inlineLabel
                                        suffix={lang.MINUTES.toLowerCase()}
                                        minValue={1}
                                        maxValue={999}
                                    />
                                </FieldLayout>
                            </FormLayout>
                        )}
                    />

                    <NoLabelGroup
                        content={(
                            <Checkbox name='backupWindowEnabled'>
                                {lang.TERMINATE_JOB_IF_IT_EXCEEDS_ALLOWED_BACKUP_WINDOW}
                            </Checkbox>
                        )}
                    />

                    <NoLabelGroup
                        content={(
                            <LinkButton
                                onClick={() => wizardContext.openPanel(hide => <ConfigureBackupWindow hidePanel={hide} />)}
                                disabled={!wizardStore.data.schedulePolicy.backupWindowEnabled}
                                style={{ width: 'fit-content' }}
                            >
                                {lang.CONFIGURE_BACKUP_WINDOW}
                            </LinkButton>
                        )}
                    />
                </FormLayout>
            </Form>
        </StepLayout>
    );
});
